﻿import React from 'react';

import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GenerateReport } from 'components/GenerateReport/GenerateReport';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Container, Row } from 'react-bootstrap';
import { Report } from 'apis/ReportApi';
import { useSuperState } from 'hooks';

import 'components/MultiGenerateReport/multi-generate-report.scss';

/** In-code datasource for the report select. Omit reportId for views that have no backing report yet. */
export type ReportTabOption = {
    name: string;
    reportId?: number;
};

type MultiGenerateReportProps = {

    /** Rendered at the end of the report select row, e.g. the print button. */
    actions?: React.ReactNode;
    cultureId: number;
    reports: ReportTabOption[];
    scopeId: number;
    scopeObjectId: number;
};

/**
 * Renders the "Reports and Activity" tab content shared by the Student, Classroom, and
 * Access Code detail pages: a report select, the selected report's database name and
 * description, and the report itself underneath.
 */
export const MultiGenerateReport = (props: MultiGenerateReportProps) => {

    const { actions, cultureId, reports, scopeId, scopeObjectId } = props;
    const { formatMessage } = useIntl();

    const [state, setState] = useSuperState({
        loadedReport: undefined as { name: string; description: string } | undefined,
        selectedIndex: 0
    });
    const { loadedReport, selectedIndex } = state;

    const selectedOption = reports[selectedIndex];

    const onSelect = (value: ReportTabOption | ReportTabOption[]) => {

        const option = Array.isArray(value) ? value[0] : value;
        const index = reports.indexOf(option);
        index >= 0 && index !== selectedIndex && setState({ loadedReport: undefined, selectedIndex: index });

    };

    const onReportLoaded = (report: Report) => {

        setState({ loadedReport: { name: report.Name, description: report.Description } });

    };

    return (
        <div className="multi-generate-report">
            <Row className="align-items-center mb-3">
                <Col lg={3} md={4} xs={12}>
                    <GMetrixDropdown
                        dataItemKey="name"
                        dataItems={reports}
                        filterable={false}
                        label={formatMessage({ id: 'reports.c01b78b6', defaultMessage: 'Reports' })}
                        onChange={onSelect}
                        textField="name"
                        value={selectedOption}
                    />
                </Col>
                <Col className="multi-generate-report__description" lg={true} md={8} xs={12}>
                    <div className="fw-bold">
                        {loadedReport?.name ?? selectedOption?.name}
                    </div>
                    <div>
                        {loadedReport?.description}
                    </div>
                </Col>
                {actions && (
                    <Col className="d-flex align-items-center justify-content-end" lg="auto" xs={12}>
                        {actions}
                    </Col>
                )}
            </Row>
            {selectedOption?.reportId ? (
                <div id="reportPDF" key={selectedOption.reportId}>
                    <GenerateReport
                        autoRunReport={true}
                        cultureId={cultureId}
                        onReportLoaded={onReportLoaded}
                        reportId={selectedOption.reportId}
                        scopeId={scopeId}
                        scopeObjectId={scopeObjectId}
                    />
                </div>
            ) : (
                <Container>
                    <h4 className="text-center">
                        <FormattedMessage defaultMessage="No Reports" id="no_reports.0c22682b" />
                    </h4>
                </Container>
            )}
        </div>
    );

};
